import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '~/src/store';
import { extractEmailFromUrl } from '~/src/utils/scripts/extractEmailFromUrl';
import { createLocaleTextGetter } from '~/src/utils/scripts/getLocaleText';
import { updatePurchaseFlow } from '~/src/reduxStore/navigation/actions';
import { analyticsEventsTracker, EVENT_TRACKERS, GTMOnboardingFunnelEvent } from '~/src/utils/analytics';

import * as S from './styled';
import { OutOfStockModal } from './outOfStockModal';

import jointHealth from './img/joint-health.png';
import gutHealth from './img/gut-health.png';
import toothHealth from './img/tooth-health.png';

const MAX_QUANTITY = 10;
const PAY_DELAY_MS = 2500;
const FALLBACK_LANG = 'en';

interface SupplementProduct {
  id: string;
  titleKey: string;
  categoryKey: string;
  ratingKey: string;
  descriptionKey: string;
  oldPrice: number;
  price: number;
  image: string;
  analyticsProductName: string;
}

const PRODUCTS: SupplementProduct[] = [
  {
    id: 'joint-health',
    titleKey: 'payment_text_373',
    categoryKey: 'payment_text_374',
    ratingKey: 'payment_text_375',
    descriptionKey: 'payment_text_376',
    oldPrice: 59.31,
    price: 34.99,
    image: jointHealth,
    analyticsProductName: 'joint_health_mobility',
  },
  {
    id: 'gut-health',
    titleKey: 'payment_text_377',
    categoryKey: 'payment_text_374',
    ratingKey: 'payment_text_378',
    descriptionKey: 'payment_text_379',
    oldPrice: 62.69,
    price: 36.99,
    image: gutHealth,
    analyticsProductName: 'gut_digestive_health',
  },
  {
    id: 'tooth-health',
    titleKey: 'payment_text_380',
    categoryKey: 'payment_text_381',
    ratingKey: 'payment_text_382',
    descriptionKey: 'payment_text_383',
    oldPrice: 54.22,
    price: 31.99,
    image: toothHealth,
    analyticsProductName: 'tooth_health',
  },
];

const formatPrice = (value: number): string => `$${value.toFixed(2)}`;

const SparkleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true">
    <path d="M5 0c.3 2.5 2.2 4.4 5 5-2.8.6-4.7 2.5-5 5-.3-2.5-2.2-4.4-5-5 2.8-.6 4.7-2.5 5-5z" />
  </svg>
);

const StarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="10" viewBox="0 0 11 10" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d="M5.2493 8.60452L7.76913 9.92202C8.25978 10.1783 8.83273 9.7639 8.73912 9.22077L8.25789 6.42952L10.2971 4.4539C10.6942 4.0689 10.4756 3.39828 9.92648 3.3189L7.10949 2.9114L5.84988 0.370781C5.60488 -0.123594 4.8956 -0.123594 4.65059 0.370781L3.39036 2.9114L0.573373 3.3189C0.0249269 3.39828 -0.194326 4.0689 0.202716 4.4539L2.24195 6.42952L1.76073 9.22077C1.66712 9.7639 2.24007 10.1789 2.73072 9.92202L5.25055 8.60452H5.2493Z" fill="#F0930C"/>
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const MinusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 8H12.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const SupplementsUpsale = () => {
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [isOutOfStockModalOpen, setIsOutOfStockModalOpen] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const payDelayTimeoutRef = useRef<number | null>(null);
  const dispatch = useDispatch();

  const checkoutEmail = useSelector((state: RootState) => state.navigation.purchaseFlowScreen.email);
  const onboardingEmail = useMemo(() => extractEmailFromUrl(), []);
  const emailForModal = checkoutEmail || onboardingEmail || '';

  const getText = useMemo(() => createLocaleTextGetter({ fallbackLang: FALLBACK_LANG }), []);

  const selectedCount = useMemo(
    () => Object.values(quantities).reduce((sum, quantity) => sum + quantity, 0),
    [quantities],
  );

  const totalPrice = useMemo(
    () => PRODUCTS.reduce((sum, product) => sum + (quantities[product.id] || 0) * product.price, 0),
    [quantities],
  );

  const selectedProductNames = useMemo(
    () =>
      PRODUCTS.filter(product => (quantities[product.id] || 0) > 0).map(product => product.analyticsProductName),
    [quantities],
  );

  const clearPayDelayTimeout = () => {
    if (payDelayTimeoutRef.current !== null) {
      window.clearTimeout(payDelayTimeoutRef.current);
      payDelayTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.showSupplementsUpsale(), EVENT_TRACKERS.GTM);
  }, []);

  useEffect(() => {
    return () => {
      clearPayDelayTimeout();
    };
  }, []);

  useEffect(() => {
    if (!isOutOfStockModalOpen) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [isOutOfStockModalOpen]);

  const changeQuantity = (productId: string, nextQuantity: number) => {
    setQuantities(prevState => {
      const nextState = { ...prevState };
      if (nextQuantity <= 0) {
        delete nextState[productId];
      } else {
        nextState[productId] = Math.min(nextQuantity, MAX_QUANTITY);
      }
      return nextState;
    });
  };

  const handleAdd = (productId: string, analyticsProductName: string) => {
    if (isPaying) {
      return;
    }
    changeQuantity(productId, 1);
    analyticsEventsTracker.trackWithTarget(
      GTMOnboardingFunnelEvent.productSelectedSupplements(analyticsProductName),
      EVENT_TRACKERS.GTM,
    );
  };

  const handleIncrement = (productId: string) => {
    if (isPaying) {
      return;
    }
    changeQuantity(productId, (quantities[productId] || 0) + 1);
  };

  const handleDecrement = (productId: string) => {
    if (isPaying) {
      return;
    }
    changeQuantity(productId, (quantities[productId] || 0) - 1);
  };

  const handleClosePage = (trackCloseUpsaleEvent = true) => {
    clearPayDelayTimeout();
    setIsPaying(false);
    setIsOutOfStockModalOpen(false);
    if (trackCloseUpsaleEvent) {
      analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.closeSupplementsUpsale(), EVENT_TRACKERS.GTM);
    }
    dispatch(updatePurchaseFlow({}));
  };

  const handlePayNow = () => {
    if (!selectedCount || isPaying) {
      return;
    }

    setIsPaying(true);
    analyticsEventsTracker.trackWithTarget(
      GTMOnboardingFunnelEvent.paymentIntentionSupplements({
        productNames: selectedProductNames,
        count: selectedCount,
        price: totalPrice,
      }),
      EVENT_TRACKERS.GTM,
    );

    clearPayDelayTimeout();
    payDelayTimeoutRef.current = window.setTimeout(() => {
      analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.showSupplementsUpsalePopup(), EVENT_TRACKERS.GTM);
      setIsPaying(false);
      setIsOutOfStockModalOpen(true);
      payDelayTimeoutRef.current = null;
    }, PAY_DELAY_MS);
  };

  const handleDismissModal = () => {
    setIsOutOfStockModalOpen(false);
  };

  const handleCloseAndContinue = () => {
    analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.closeSupplementsUpsalePopup(), EVENT_TRACKERS.GTM);
    handleClosePage(false);
  };

  const handleSubscribe = () => {
    analyticsEventsTracker.trackWithTarget(
      GTMOnboardingFunnelEvent.subscribeSupplements({
        productNames: selectedProductNames,
        count: selectedCount,
        price: totalPrice,
        email: emailForModal,
      }),
      EVENT_TRACKERS.GTM,
    );
    handleClosePage(false);
  };

  const hasSelection = selectedCount > 0;

  return (
    <S.Wrapper $scrollLocked={isOutOfStockModalOpen}>
      <S.Content $footerVisible={hasSelection}>
        <S.ClosePageButton type="button" onClick={() => handleClosePage()} aria-label="Close supplements widget">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M14.1453 0.318191C14.5696 -0.106064 15.2572 -0.106063 15.6815 0.318191C16.1057 0.742446 16.1057 1.43007 15.6815 1.85432L9.53596 7.99983L15.6815 14.1453C16.1057 14.5696 16.1057 15.2572 15.6815 15.6815C15.2572 16.1057 14.5696 16.1057 14.1453 15.6815L7.99983 9.53596L1.85432 15.6815C1.43007 16.1057 0.742446 16.1057 0.318191 15.6815C-0.106063 15.2572 -0.106064 14.5696 0.318191 14.1453L6.4637 7.99983L0.318191 1.85432C-0.106064 1.43007 -0.106064 0.742446 0.318191 0.318191C0.742446 -0.106064 1.43007 -0.106064 1.85432 0.318191L7.99983 6.4637L14.1453 0.318191Z" fill="#E2DDD3"/>
          </svg>
        </S.ClosePageButton>

        <S.Header>
          <S.Badge>
            <SparkleIcon />
            {getText('payment_text_361')}
            <SparkleIcon />
          </S.Badge>
          <S.Title dangerouslySetInnerHTML={{ __html: getText('payment_text_362') }} />
          <S.Subtitle>{getText('payment_text_363')}</S.Subtitle>
        </S.Header>

        <S.ProductsList>
          {PRODUCTS.map(product => {
            const quantity = quantities[product.id] || 0;
            const isAdded = quantity > 0;
            const title = getText(product.titleKey);

            return (
              <S.ProductCard key={product.id}>
                <S.ProductTop>
                  <S.ProductImage src={product.image} alt={title} loading="lazy" />
                  <S.ProductInfo>
                    <S.ProductTitle>{title}</S.ProductTitle>
                    <S.ProductMeta>
                      <span>{getText(product.categoryKey)}</span>
                      <S.ProductRating>
                        <StarIcon />
                        {getText(product.ratingKey)}
                      </S.ProductRating>
                    </S.ProductMeta>
                    <S.ProductDescription>{getText(product.descriptionKey)}</S.ProductDescription>
                  </S.ProductInfo>
                </S.ProductTop>

                <S.ProductFooter>
                  <S.PriceBlock>
                    <S.OldPrice>{formatPrice(product.oldPrice)}</S.OldPrice>
                    <S.NewPrice>{formatPrice(product.price)}</S.NewPrice>
                  </S.PriceBlock>

                  {isAdded ? (
                    <S.QuantityControl>
                      <S.QuantityMinus
                        type="button"
                        onClick={() => handleDecrement(product.id)}
                        aria-label={`Remove one ${title}`}
                      >
                        <MinusIcon />
                      </S.QuantityMinus>
                      <S.QuantityValue aria-live="polite">{quantity}</S.QuantityValue>
                      <S.QuantityPlus
                        type="button"
                        onClick={() => handleIncrement(product.id)}
                        disabled={quantity >= MAX_QUANTITY}
                        aria-label={`Add one more ${title}`}
                      >
                        <PlusIcon />
                      </S.QuantityPlus>
                    </S.QuantityControl>
                  ) : (
                    <S.AddButton
                      type="button"
                      onClick={() => handleAdd(product.id, product.analyticsProductName)}
                      aria-label={`Add ${title}`}
                    >
                      <PlusIcon />
                      {getText('payment_text_364')}
                    </S.AddButton>
                  )}
                </S.ProductFooter>
              </S.ProductCard>
            );
          })}
        </S.ProductsList>
      </S.Content>

      {hasSelection ? (
        <S.Footer>
          <S.SummaryRow>
            <span>{getText('payment_text_365')}</span>
            <span>{selectedCount}</span>
          </S.SummaryRow>
          <S.SummaryRow>
            <span>{getText('payment_text_366')}</span>
            <span>Free</span>
          </S.SummaryRow>
          <S.TotalRow>
            <span>{getText('payment_text_367')}</span>
            <strong>{formatPrice(totalPrice)}</strong>
          </S.TotalRow>

          <S.PayButton
            type="button"
            onClick={handlePayNow}
            disabled={!selectedCount || isPaying}
            aria-label={getText('payment_text_368')}
            aria-busy={isPaying}
          >
            <S.PayButtonText>{getText('payment_text_368')}</S.PayButtonText>
            {isPaying ? <S.Spinner aria-hidden="true" /> : null}
          </S.PayButton>
        </S.Footer>
      ) : null}

      <OutOfStockModal
        isOpen={isOutOfStockModalOpen}
        onDismiss={handleDismissModal}
        onCloseAndContinue={handleCloseAndContinue}
        onSubscribe={handleSubscribe}
        email={emailForModal}
        getText={getText}
      />
    </S.Wrapper>
  );
};
